import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import BusinessTravellerStepper from 'businessTraveller/components/stepper/BusinessTravellerStepGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Divider from 'LuxKit/Divider'
import config from 'constants/config'
import SocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import { useMutation } from '@tanstack/react-query'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import styled from 'styled-components'
import { formToObject } from 'lib/forms/formToObject'
import ReCAPTCHA from 'react-google-recaptcha'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import { useAppDispatch } from 'hooks/reduxHooks'
import { login } from 'api/auth'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_LOGIN } from 'actions/apiActionConstants'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
  BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS,
  BusinessTravellerBusinessOnboardingDispatchContext,
  BusinessTravellerBusinessOnboardingStateContext,
} from 'businessTraveller/contexts/BusinessTravellerBusinessOnboardingContexts'
import LayoutContainer from 'components/Common/LayoutContainer'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const SignupContainer = styled(LayoutContainer)`
  background-color:  ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const LoginForm = styled.form`
  position: relative;
`

const stepStatuses: App.BusinessTraveller.BusinessSignupStepKeyStatuses = {
  validate_email: 'complete',
  create_customer: 'active',
  create_business: 'pending',
}

interface MappedStateProps {
  isLoggedIn?: boolean;
}

interface Props {
  steps: App.BusinessTraveller.BusinessSignupSteps
}

function BusinessTravellerSignupLinkAccount(props: Props & MappedStateProps) {
  const { steps, isLoggedIn } = props
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const { adminEmail } = useContext(BusinessTravellerBusinessOnboardingStateContext)
  const accountSetupDispatch = useContext(BusinessTravellerBusinessOnboardingDispatchContext)

  const formRef = useRef<HTMLFormElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const reCaptchaRef = useRef<ReCAPTCHA>(null)

  const isSocialMediaLoginEnabled = !!(config.GOOGLE_APP_ID)

  // Change the label of this step to match the title
  const updatedSteps = steps.map(step => {
    if (step.stepKey === 'create_customer') {
      return { ...step, label: 'Link your account' }
    }
    return step
  })

  const loginMutation = useMutation({
    mutationFn: async(userDetails: Pick<App.BusinessTraveller.UserRegistrationData, 'email' | 'password'>) => {
      const reCaptchaResponse = await handleReCAPTCHA(reCaptchaRef.current)
      return login({ email: userDetails.email }, userDetails.password, reCaptchaResponse)
    },
    onSuccess: (data) => {
      dispatch({
        type: API_CALL_SUCCESS,
        api: USER_LOGIN,
        data,
        source: 'email',
      })
      setIsFormSubmitting(false)
    },
    onError: (error: App.ApiCallFailure) => {
      passwordInputRef.current?.setCustomValidity(error.message)
      formRef.current?.checkValidity()
      setIsFormSubmitting(false)
    },
  })

  const passwordOnChange = useCallback(() => {
    passwordInputRef.current?.setCustomValidity('')
    passwordInputRef.current?.reportValidity()
  }, [])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    setIsFormSubmitting(true)
    const formData = formToObject<App.BusinessTraveller.UserRegistrationData>(e.currentTarget)
    loginMutation.mutate({
      ...formData,
      email: adminEmail,
    })
  }, [loginMutation, adminEmail])

  useEffect(() => {
    if (isLoggedIn) {
      accountSetupDispatch({
        type: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEP_INTO,
        step: BUSINESS_TRAVELLER_BUSINESS_ONBOARDING_STEPS.CREATE_BUSINESS,
      })
    }
  }, [accountSetupDispatch, isLoggedIn])

  return (
    <SignupContainer>
      <LoginForm onSubmit={handleSubmission} ref={formRef}>
        {isFormSubmitting && <LoadingIndicator floating />}
        <Group direction="vertical" gap={20}>
          <Heading variant="heading3">Link your account</Heading>
          <BusinessTravellerStepper steps={updatedSteps} stepStatuses={stepStatuses} />
          <BodyText variant="medium">Business Traveller is partnered with Luxury Escapes. Connect your accounts using your Luxury Escapes password to sign in</BodyText>
          <PasswordInput
            autoComplete="new-password"
            label="Password"
            minLength={8}
            name="password"
            onChange={passwordOnChange}
            ref={passwordInputRef}
            required
            requiredErrorMessage="Please enter a password"
          />
          <TextButton
            fit="mobile-full-width"
            kind="primary"
            shape="square"
            size="medium"
            type="submit"
            disabled={isFormSubmitting}
          >
            Continue
          </TextButton>
          {isSocialMediaLoginEnabled && <>
            <Group direction="horizontal" gap={12} verticalAlign="center">
              <Divider kind="primary" />
              <BodyText variant="large">or</BodyText>
              <Divider kind="primary" />
            </Group>
            <SocialButtons mode="login" />
          </>}
        </Group>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reCaptchaRef} />}
      </LoginForm>
    </SignupContainer>
  )
}

export default connect<MappedStateProps, undefined, {}, App.State>((state) => {
  return { isLoggedIn: selectLoggedIn(state) }
})(BusinessTravellerSignupLinkAccount)
